import { useParams, Link } from 'react-router-dom';
import { comics } from '@/mocks/comics';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEO from '@/components/base/SEO';
import { useState, useEffect, useRef } from 'react';

const mockChapters = Array.from({ length: 128 }, (_, i) => ({
  number: 128 - i,
  title: i === 0 ? '命运的转折点' : `第${128 - i}话`,
  updateTime: i === 0 ? '2小时前' : `${Math.floor(Math.random() * 30) + 1}天前`,
  isVip: i < 3,
}));

const readingImages = [
  'https://readdy.ai/api/search-image?query=Korean%20manhwa%20comic%20page%20panel%2C%20dramatic%20action%20scene%20with%20characters%20fighting%2C%20speech%20bubbles%2C%20manga%20style%20black%20and%20white%20with%20screentones%2C%20dynamic%20composition%2C%20detailed%20speed%20lines%2C%20clean%20lineart%2C%20professional%20comic%20art&width=1200&height=675&seq=reading-page-a1&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Korean%20manhwa%20comic%20page%20panel%2C%20romantic%20dialogue%20scene%20with%20two%20characters%2C%20emotional%20expressions%2C%20manga%20style%20black%20and%20white%20with%20screentones%2C%20soft%20background%20with%20flowers%2C%20clean%20lineart%2C%20professional%20comic%20art&width=1200&height=675&seq=reading-page-b2&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Korean%20manhwa%20comic%20page%20panel%2C%20tense%20dramatic%20scene%20with%20closeup%20face%2C%20intense%20emotion%2C%20manga%20style%20black%20and%20white%20with%20screentones%2C%20dark%20shadows%2C%20detailed%20eyes%2C%20clean%20lineart%2C%20professional%20comic%20art&width=1200&height=675&seq=reading-page-c3&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Korean%20manhwa%20comic%20page%20panel%2C%20fantasy%20magical%20scene%20with%20spell%20effects%2C%20manga%20style%20black%20and%20white%20with%20screentones%2C%20sparkling%20magic%20lines%2C%20dramatic%20atmosphere%2C%20clean%20lineart%2C%20professional%20comic%20art&width=1200&height=675&seq=reading-page-d4&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Korean%20manhwa%20comic%20page%20panel%2C%20slice%20of%20life%20coffee%20shop%20scene%2C%20manga%20style%20black%20and%20white%20with%20screentones%2C%20warm%20atmosphere%2C%20character%20drinking%20coffee%2C%20clean%20lineart%2C%20professional%20comic%20art&width=1200&height=675&seq=reading-page-e5&orientation=landscape',
];

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [readingMode, setReadingMode] = useState(false);
  const [showFullDesc, setShowFullDesc] = useState(false);
  const readingRef = useRef<HTMLDivElement>(null);

  const comic = comics.find((c) => c.id === slug);
  const relatedComics = comics
    .filter((c) => c.category === comic?.category && c.id !== comic?.id)
    .slice(0, 5);

  useEffect(() => {
    setReadingMode(false);
    setShowFullDesc(false);
  }, [slug]);

  useEffect(() => {
    if (readingMode && readingRef.current) {
      const navbarHeight = 80;
      const y = readingRef.current.getBoundingClientRect().top + window.scrollY - navbarHeight;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, [readingMode]);

  const handleStartReading = () => {
    setReadingMode(true);
  };

  const handleChapterClick = () => {
    setReadingMode(true);
  };

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-24 pb-20 px-4 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950">漫画不存在</h1>
          <Link to="/" className="mt-4 inline-block text-sm text-primary-500 hover:underline">返回首页</Link>
        </main>
        <Footer />
      </>
    );
  }

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩国漫画网`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: '2024-01-15',
    dateModified: new Date().toISOString().split('T')[0],
    publisher: {
      '@type': 'Organization',
      name: '韩国漫画网',
    },
  };

  const pageTitle = `${comic.title} - ${comic.categoryName}韩国漫画免费阅读 - 韩国漫画网`;
  const pageDesc = `${comic.title}是由${comic.author}创作的${comic.categoryName}类韩国漫画。${comic.description}在韩国漫画网免费在线阅读，下拉式畅快体验。`;

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <SEO
        title={pageTitle}
        description={pageDesc}
        keywords={`${comic.title},${comic.categoryName}漫画,韩国漫画免费,在线看韩漫`}
        canonicalUrl={`http://laimim.com/article/${slug}`}
        ogType="article"
      />
      <Navbar />

      <main className="min-h-screen pt-20">
        <div className="w-full max-w-5xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: comic.categoryName, href: `/category/${comic.category}` },
              { label: comic.title },
            ]}
          />

          <article className="py-8">
            <div className="flex flex-col md:flex-row gap-8 md:gap-12">
              <div className="flex-shrink-0 mx-auto md:mx-0">
                <div className="w-48 md:w-56 rounded-xl overflow-hidden bg-background-200 border border-background-300/60">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 韩国漫画网`}
                    className="w-full aspect-[3/4] object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-950">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <span className="text-sm text-foreground-600">{comic.author}</span>
                  <span className="text-foreground-300">·</span>
                  <Link
                    to={`/category/${comic.category}`}
                    className="text-sm text-primary-500 hover:underline whitespace-nowrap"
                  >
                    {comic.categoryName}
                  </Link>
                  <span className="text-foreground-300">·</span>
                  <span className="text-sm text-foreground-500">{comic.updateChapter}</span>
                  {comic.isCompleted && (
                    <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-accent-100 text-accent-700 whitespace-nowrap">
                      已完结
                    </span>
                  )}
                  {comic.isHot && (
                    <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
                      HOT
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-4 mt-4">
                  <span className="flex items-center gap-1 text-sm text-accent-500 font-medium">
                    <i className="ri-star-fill text-sm" />{comic.rating}
                  </span>
                  <span className="text-sm text-foreground-500">
                    <i className="ri-eye-line mr-1" />{formatViews(comic.views)}阅读
                  </span>
                  <span className="text-sm text-foreground-500">{comic.updateTime}更新</span>
                </div>

                <div className="flex items-center gap-1.5 mt-4 flex-wrap">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 text-xs rounded-full bg-background-200 text-foreground-600 whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="mt-5">
                  <p className={`text-sm text-foreground-600 leading-relaxed ${!showFullDesc && 'line-clamp-3'}`}>
                    {comic.description}
                  </p>
                  {comic.description.length > 80 && (
                    <button
                      type="button"
                      onClick={() => setShowFullDesc(!showFullDesc)}
                      className="text-xs text-primary-500 hover:underline mt-1 cursor-pointer"
                    >
                      {showFullDesc ? '收起' : '展开全部'}
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-3 mt-6">
                  <button
                    type="button"
                    onClick={handleStartReading}
                    className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 font-medium text-sm rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    开始阅读
                    <i className="ri-play-line" />
                  </button>
                  <button
                    type="button"
                    className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300 text-foreground-600 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <i className="ri-heart-line text-lg" />
                  </button>
                  <button
                    type="button"
                    className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300 text-foreground-600 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <i className="ri-share-forward-line text-lg" />
                  </button>
                </div>
              </div>
            </div>

            <div ref={readingRef}>
              {readingMode && (
                <section className="mt-10 border border-background-200/70 rounded-2xl overflow-hidden bg-background-50">
                  <div className="flex items-center justify-between px-5 py-3 bg-background-100 border-b border-background-200/70">
                    <span className="text-sm font-medium text-foreground-900">下拉式阅读区</span>
                    <button
                      type="button"
                      onClick={() => setReadingMode(false)}
                      className="text-sm text-foreground-500 hover:text-foreground-700 cursor-pointer"
                    >
                      关闭阅读
                    </button>
                  </div>
                  <div className="p-5">
                    <div className="flex flex-col gap-4">
                      {readingImages.map((imgUrl, i) => (
                        <div
                          key={i}
                          className="w-full aspect-[16/9] rounded-lg bg-background-200 flex items-center justify-center"
                        >
                          <img
                            src={imgUrl}
                            alt={comic.title + ' - 漫画内容'}
                            title={comic.title + ' - 漫画内容 第' + (i + 1) + '页'}
                            className="w-full h-full object-contain bg-background-200"
                            loading="lazy"
                          />
                        </div>
                      ))}
                    </div>
                    <p className="text-center text-sm text-foreground-400 mt-6">
                      继续向下滚动阅读更多内容...
                    </p>
                  </div>
                </section>
              )}
            </div>

            <section className="mt-10">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-5">
                章节列表
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {mockChapters.slice(0, 30).map((ch) => (
                  <button
                    key={ch.number}
                    type="button"
                    onClick={handleChapterClick}
                    className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 hover:bg-background-200 transition-colors text-left cursor-pointer"
                  >
                    <span className="text-sm text-foreground-900 truncate">
                      {ch.isVip && <span className="text-[10px] text-accent-500 mr-1 font-medium">VIP</span>}
                      第{ch.number}话 {ch.title}
                    </span>
                    <span className="text-xs text-foreground-500 flex-shrink-0 ml-2">{ch.updateTime}</span>
                  </button>
                ))}
              </div>
            </section>

            {relatedComics.length > 0 && (
              <section className="mt-14 pb-10">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-5">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                  {relatedComics.map((c) => (
                    <Link
                      key={c.id}
                      to={`/article/${c.id}`}
                      className="group block cursor-pointer"
                    >
                      <div className="relative overflow-hidden rounded-lg aspect-[3/4] bg-background-200">
                        <img
                          src={c.cover}
                          alt={c.title}
                          title={`${c.title} - 韩国漫画网`}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/60 to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-3">
                          <p className="text-xs text-white/90 font-medium truncate">{c.title}</p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </article>
        </div>
      </main>

      <Footer />
    </>
  );
}